/* eslint-disable no-lonely-if */
import {
  Box,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Tooltip,
  useBoolean,
  useOutsideClick,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import FigmaCheckCircleIcon from 'components/Icons/FigmaCheckCircleIcon';
import FigmaMenuDotsIcon from 'components/Icons/FigmaMenuDotsIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import DuplicateIconV1 from 'components/Icons/DuplicateIconV1';
import { Scenario } from 'models/response/scenario.interface';
import {
  DeleteScenarios,
  DuplicateScenarios,
  FetchScenarios,
  RenameScenario,
  getAllScenarios,
  getBaseScenario,
  selectBaseScenario,
} from 'redux/BuilderModeSlice';
import ScenarioPermission from 'pages/PermissionComponent/ScenarioPermission';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import PlanPermission from 'container/PlanPermission';
import { selectBaseScenario as selectBuilderModeScenario } from 'redux/PlannerModeSlice';
import { selectBaseScenario as selectPlannerModeScenario } from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { accent, radius } from 'styles/revampTokens';
import DeleteModal from 'components/DeleteModal';

// components/Modals/DeleteModal (imported nowhere in this file) is the
// untouched original used by the rest of the app and must stay that way.
import ScenarioFormForecast from './ScenarioFormForecast';

interface BaseScenarioState {
  planPermission: boolean;
  renameScenarioApi: any;
  getAllscenarios: any;
  deleteScenario: any;
  selectedDropdown?: number | any;
  duplicateScenario: any;
  seletctBaseScenario: (scenario: Scenario) => void;
}
const ScenarioForecast = ({
  planPermission,
  getAllscenarios,
  deleteScenario,
  duplicateScenario,
  seletctBaseScenario,
}: BaseScenarioState) => {
  const dispatch: AppDispatch = useDispatch();

  const { modelId } = useParams();
  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const [scenariosPopover, setScenariosPopover] = useBoolean();
  const [addScenarioPopover, setAddScenarioPopover] = useBoolean();
  const [isPermissionComponent, setIsPermissionComponent] = useState(false);
  const [displayInfo, setDisplayInfo]: any = useState(null);
  const [renameItemId, setRenameItemId] = useState<number | null>(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const [editScenarioData, setEditScenarioData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    // Always prefer modalFilters.scenario if present and valid
    if (modelId) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      if (modalFilters?.scenario) {
        // Try to find the scenario by id (if it's an object or just an id)
        let scenarioToSelect = null;
        scenarioToSelect =
          typeof modalFilters.scenario === 'object' && modalFilters.scenario.id
            ? scenarios.find((s: any) => s.id === modalFilters.scenario.id)
            : scenarios.find((s: any) => s.id === modalFilters.scenario);
        if (scenarioToSelect) {
          dispatch(selectBaseScenario(scenarioToSelect));
        }
      } else if (baseScenario) {
        // Fallback to baseScenario from redux if present in scenarios
        const base = scenarios.find((scenario: any) => scenario.id === baseScenario.id);
        if (base) {
          dispatch(selectBaseScenario(base));
        }
        if (scenarios?.length > 0) {
          const baseSec = scenarios.find((scenario: any) => scenario.is_base);
          dispatch(selectBaseScenario(baseSec));
        }
      } else {
        // Fallback to first base scenario in list
        const baseSec = scenarios.find((scenario: any) => scenario.is_base);
        dispatch(selectBaseScenario(baseSec));
      }
    } else {
      // If no modelId, fallback to base scenario logic
      if (baseScenario) {
        const base = scenarios.find((scenario: any) => scenario.id === baseScenario.id);
        if (base) {
          dispatch(selectBaseScenario(base));
        }
        if (scenarios?.length > 0) {
          const baseSec = scenarios.find((scenario: any) => scenario.is_base);
          if (baseSec) {
            dispatch(selectBaseScenario(baseSec));
          }
        }
      } else if (scenarios?.length > 0) {
        const baseSec = scenarios.find((scenario: any) => scenario.is_base);
        if (baseSec) {
          dispatch(selectBaseScenario(baseSec));
        }
      }
    }
  }, [scenarios, baseScenario, modelId]);

  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;

  const toggleInfoPopup = (event: any, id: number | null) => {
    event.stopPropagation();
    if (displayInfo === id) {
      return setDisplayInfo(null);
    }
    return setDisplayInfo(id);
  };

  useOutsideClick({
    ref: reference,
    handler: () => {
      setDisplayInfo(null);
      setRenameItemId(0);
      setAddScenarioPopover.off();
      setScenariosPopover.off();
      setIsPermissionComponent(false);
      setEditScenarioData(null);
      setIsEdit(false);
    },
  });

  const renameItem = (event: any, scenario: any) => {
    event.stopPropagation();
    setDisplayInfo(null);
    setScenariosPopover.off();
    if (renameItemId === scenario?.id) {
      setRenameItemId(0);
      setEditScenarioData(null);
      setIsEdit(false);
    }
    setRenameItemId(scenario?.id);
    setAddScenarioPopover.on();
    setEditScenarioData(scenario);
    setIsEdit(true);
  };

  const copyScenario = async (event: any, item: any) => {
    event.stopPropagation();
    const requestBody = {
      scenarioId: item?.id,
      data: {
        name: `${item?.name} - Copy`,
      },
      toaster: {
        successMessage: 'Successfully duplicated scenario',
        errorMessage: 'Failed to duplicate',
      },
    };

    await duplicateScenario(requestBody);
    await getAllscenarios(modelId);
  };

  const deleteScenarioModal = async (event: any, id: number, name: any) => {
    event.stopPropagation();
    setIsDeleteModal(true);
    setDeleteIdModal(id);
    setDeleteNameModal(name);
  };

  const onDeleteModel = async () => {
    await deleteScenario(deleteIdModal);
    await getAllscenarios(modelId);
    await setIsDeleteModal(false);
    await setDeleteIdModal(null);
    await setDeleteNameModal('');
    if (baseScenario?.id === deleteIdModal) {
      const randomScenario = scenarios.find((scenario: Scenario) => {
        return scenario?.id !== deleteIdModal;
      });
      if (randomScenario) {
        dispatch(selectBaseScenario(randomScenario));
      }
    }
  };

  const selectScenario = async (scenario: Scenario) => {
    seletctBaseScenario(scenario);
    dispatch(selectBuilderModeScenario(scenario));
    dispatch(selectPlannerModeScenario(scenario));
    if (modelId) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      modalFilters.scenario = scenario;
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [modelId]: modalFilters }),
      );
    }
  };

  const handleCancel = () => {
    setAddScenarioPopover.off();
    setEditScenarioData(null);
    setIsEdit(false);
  };

  const scenariosLength = scenarios?.length ?? 0;

  // const OpenPopover = () => {
  //   if (
  //     scenarios?.length ===
  //       UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
  //     scenarios?.length >=
  //       UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
  //   ) {
  //     setAddScenarioPopover.off();
  //     setIsPermissionComponent(true);
  //   } else if (planPermission === true) {
  //     setAddScenarioPopover.off();
  //   } else {
  //     setIsPermissionComponent(false);
  //     setAddScenarioPopover.on();
  //     setDisplayInfo(null);
  //   }

  //   setEditScenarioData(null);
  //   setIsEdit(false);
  // };
  const OpenPopover = () => {
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
      setDisplayInfo(null);
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setAddScenarioPopover.off();
      setIsPermissionComponent(true);
    } else if (planPermission === true) {
      setAddScenarioPopover.off();
    } else {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
      setDisplayInfo(null);
    }
    setEditScenarioData(null);
    setIsEdit(false);
  };
  useEffect(() => {
    setIsPermissionComponent(false);
  }, [UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model]);

  return (
    <>
      <Stack width='fit-content'>
        {scenarios?.length > 0 && (
          <Popover
            placement='bottom-start'
            closeOnBlur={false}
            isOpen={scenariosPopover}
            onOpen={setScenariosPopover.on}
            onClose={setScenariosPopover.off}
            isLazy
            lazyBehavior='keepMounted'
          >
            <Box ref={reference}>
              <PopoverTrigger>
                <Flex
                  textAlign='left'
                  alignSelf='left'
                  bgColor={accent.subtle}
                  padding='9px 16px'
                  minW='130px'
                  borderRadius={`${radius.control} 0 0 ${radius.control}`}
                  transition='all 0.3s ease-out'
                  alignItems='center'
                  gap={2}
                  cursor='pointer'
                >
                  <Flex width='auto' alignItems='center'>
                    <Box w='8px' h='8px' borderRadius='full' bg='#3BA7FA' mr='2' />
                    <Tooltip isDisabled label={baseScenario?.name}>
                      <Text
                        alignContent='center'
                        color='#121826'
                        fontSize='13px'
                        fontFamily='Roboto Regular'
                        whiteSpace='nowrap'
                        overflow='hidden'
                        textOverflow={'ellipsis'}
                      >
                        {baseScenario?.name}
                      </Text>
                    </Tooltip>
                  </Flex>
                  <FigmaTriangleDown width={9} height={6} color='#8B5CF6' />
                </Flex>
              </PopoverTrigger>

              <PopoverContent
                ml={{ base: '5', md: '3' }}
                boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
                bg='white'
                borderRadius={radius.overlay}
                border='none'
                minW='302px'
                outline='none'
                _focus={{ boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.25)' }}
                _focusVisible={{ boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.25)' }}
              >
                {/* Header from old UI */}
                <Box px='16px' pt='12px' pb='6px'>
                  <Text
                    fontFamily='Roboto Medium, Roboto Regular'
                    fontSize='11px'
                    fontWeight={600}
                    color='#708090'
                    textTransform='uppercase'
                    letterSpacing='0.04em'
                  >
                    Select Scenario
                  </Text>
                </Box>

                {/* Fixed + Add New Scenario button (pinned at top, not at bottom/last) */}
                <Flex
                  align='center'
                  gap='8px'
                  bg='white'
                  px='16px'
                  py='9px'
                  cursor={planPermission ? 'not-allowed' : 'pointer'}
                  _hover={{ bg: '#F8F9FA' }}
                  transition='background 0.15s ease'
                  onClick={
                    planPermission
                      ? undefined
                      : () => {
                          setScenariosPopover.off();
                          OpenPopover();
                        }
                  }
                >
                  <FigmaPlusIcon width={8} height={8} color='#8B5CF6' />
                  <Text
                    fontFamily='Roboto Medium, Roboto Regular'
                    fontWeight='500'
                    fontSize='13px'
                    color='#8B5CF6'
                  >
                    Add New Scenario
                  </Text>
                </Flex>

                <Divider borderColor='#E8E9EB' />

                <PopoverBody p={0}>
                  <Flex
                    direction='column'
                    gap='1px'
                    bg='#E8E9EB'
                    maxH='260px'
                    overflowY='auto'
                    borderBottomRadius={isPermissionComponent ? 0 : radius.overlay}
                    sx={{
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-track': { background: 'transparent' },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#D1D6DD',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': { background: '#8B89B1' },
                    }}
                  >
                    {scenarios?.length > 0 &&
                      scenarios.map((scenario: Scenario, index: number) => {
                        return (
                          <Flex
                            key={scenario.id}
                            alignItems='center'
                            gap='8px'
                            cursor='pointer'
                            bg='white'
                            px='16px'
                            py='10px'
                            _hover={{ bg: '#F8F9FA' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              selectScenario(scenario);
                              setScenariosPopover.off();
                            }}
                            onDoubleClick={(event) => {
                              event.stopPropagation();
                              setRenameItemId(scenario?.id);
                            }}
                          >
                            <Text
                              flex={1}
                              fontFamily='Roboto Regular'
                              fontSize='13px'
                              color='#121826'
                              whiteSpace='nowrap'
                              overflow='hidden'
                              textOverflow='ellipsis'
                              onClick={() => setDisplayInfo(null)}
                            >
                              {scenario?.name}
                            </Text>
                            {baseScenario?.id === scenario?.id ? (
                              <FigmaCheckCircleIcon width={14} height={14} color='#8B5CF6' />
                            ) : (
                              <Box
                                width='14px'
                                height='14px'
                                borderRadius='50%'
                                bg='white'
                                border='1px solid #D1D6DD'
                              />
                            )}
                            <Flex alignItems='center' position='relative'>
                              <Flex
                                alignItems='center'
                                cursor={planPermission ? 'not-allowed' : 'pointer'}
                                onClick={
                                  planPermission
                                    ? undefined
                                    : (event) => toggleInfoPopup(event, scenario.id)
                                }
                              >
                                <FigmaMenuDotsIcon width={14} height={14} color='#121826' />
                              </Flex>
                              <Flex
                                bg='#E8E9EB'
                                width='153px'
                                flexDirection='column'
                                gap='1px'
                                position='absolute'
                                display={displayInfo === scenario.id ? 'flex' : 'none'}
                                zIndex='dropdown'
                                top={
                                  scenariosLength > 3 && index >= scenariosLength - 3
                                    ? '-100px'
                                    : '28px'
                                }
                                right='0'
                                p={0}
                                overflow='hidden'
                                borderRadius={radius.overlay}
                                boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
                              >
                                <Flex
                                  as='button'
                                  alignItems='center'
                                  gap='8px'
                                  bg='white'
                                  p='9px 11px'
                                  fontFamily='Roboto Regular'
                                  fontSize='13px'
                                  color='#121826'
                                  cursor={planPermission ? 'not-allowed' : 'pointer'}
                                  _hover={{ bg: '#F5F5F5' }}
                                  onClick={
                                    planPermission
                                      ? undefined
                                      : (event) => renameItem(event, scenario)
                                  }
                                >
                                  <FigmaEditIcon width={14} height={14} color='#121826' />
                                  Edit
                                </Flex>
                                <Flex
                                  as='button'
                                  alignItems='center'
                                  gap='8px'
                                  bg='white'
                                  p='9px 11px'
                                  fontFamily='Roboto Regular'
                                  fontSize='13px'
                                  color='#121826'
                                  cursor={planPermission ? 'not-allowed' : 'pointer'}
                                  _hover={{ bg: '#F5F5F5' }}
                                  onClick={
                                    planPermission
                                      ? undefined
                                      : (event) => copyScenario(event, scenario)
                                  }
                                >
                                  <Flex
                                    w='14px'
                                    h='14px'
                                    align='center'
                                    justify='center'
                                    flexShrink={0}
                                  >
                                    <DuplicateIconV1 />
                                  </Flex>
                                  Duplicate
                                </Flex>
                                {Number(scenarios?.length) !== 1 && (
                                  <Flex
                                    as='button'
                                    alignItems='center'
                                    gap='8px'
                                    bg='white'
                                    p='9px 11px'
                                    fontFamily='Roboto Regular'
                                    fontSize='13px'
                                    color='#E23F3F'
                                    cursor={planPermission ? 'not-allowed' : 'pointer'}
                                    _hover={{ bg: '#FEF0F0' }}
                                    onClick={
                                      planPermission
                                        ? undefined
                                        : (event) => {
                                            deleteScenarioModal(
                                              event,
                                              scenario?.id,
                                              scenario?.name,
                                            );
                                          }
                                    }
                                  >
                                    <FigmaTrashIcon width={14} height={14} color='#E23F3F' />
                                    Delete
                                  </Flex>
                                )}
                              </Flex>
                            </Flex>
                          </Flex>
                        );
                      })}
                  </Flex>
                  {isPermissionComponent && (
                    <ScenarioPermission
                      ScenarioLimit={
                        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
                      }
                      CurrentScenarioLength={scenarios?.length}
                    />
                  )}
                </PopoverBody>
              </PopoverContent>
            </Box>
          </Popover>
        )}
      </Stack>
      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />
      <Modal
        isOpen={addScenarioPopover}
        onClose={handleCancel}
        isCentered
        closeOnOverlayClick
        returnFocusOnClose={false}
        blockScrollOnMount={false}
      >
        <ModalOverlay bg='blackAlpha.600' />
        <ModalContent
          width='412px'
          maxWidth='90vw'
          borderRadius={radius.overlay}
          p='20px'
          bg='white'
          boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
        >
          <ModalBody p={0}>
            <ScenarioFormForecast
              onCancel={handleCancel}
              editScenarioData={editScenarioData}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
const mapDispatchToProperties = (dispatch: any) => {
  return {
    getAllscenarios: (modelId: number) => dispatch(FetchScenarios(modelId)),
    renameScenarioApi: (data: any) => dispatch(RenameScenario(data)),
    duplicateScenario: (data: any) => dispatch(DuplicateScenarios(data)),
    deleteScenario: (scenarioId: number) => dispatch(DeleteScenarios(scenarioId)),
    seletctBaseScenario: (scenario: Scenario) => dispatch(selectBaseScenario(scenario)),
  };
};

export default connect(null, mapDispatchToProperties)(PlanPermission(ScenarioForecast));
